// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
  type Ref,
} from 'react';

import { ApiRequestError, api } from '../../api/client';
import { isTerminal, type ModelEditEntry, type ModelEditOp, type ModelRevision, type ModelSnap, type RoomToolApplied, type SpaceDraft } from '../../api/types';
import { useSettings } from '../../components/bygning/settings/SettingsContext';
import { useAuth } from '../AuthGate';
import { useCanEdit } from '../CaseRoleContext';
import { useJobs } from '../JobsContext';
import { useAsync } from '../useAsync';
import type { GeometriModel } from '../../bygning/geometri';
import { deleteOps, editLabel, revertOps } from '../../bygning/geometriEdit';
import { commandDef, type GeometriCommand, type GeometriTool } from '../../bygning/geometriKeys';
import { cycle } from '../../bygning/geometriSelect';
import { applicableCommands, startModify, type ModifyCommand, type ModifySession } from '../../bygning/modifyTool';
import { DEFAULT_WALL_OPTIONS, type WallDrawOptions } from '../../bygning/wallDraw';
import { defaultOpeningOptions, warningText, type OpeningKind, type OpeningOptions } from '../../bygning/openingPlace';
import { failureKind, failureMessage, settle, withPending, type PendingEdit } from '../../bygning/geometriSync';
import type { XY } from '../../bygning/planWalls';
import {
  canRedoAnnotations,
  canUndoAnnotations,
  historyReducer,
  initialHistory,
  restoreAnnotations,
  storeAnnotations,
  type AnnotationAction,
  type AnnotationHistory,
  type Annotations,
} from '../../bygning/cadAnnotations';
import { annotationLabel, existingIds } from '../../bygning/annotationEdit';
import { EMPTY_JOURNAL, markModel, record, takeRedo, takeUndo, type Journal } from '../../bygning/editJournal';
import type { DimOrient } from '../../bygning/cadMeasure';
import { annotationsKey, browserStorage } from '../../components/bygning/settings/settingsStorage';
import { DEFAULT_ROOM_OPTIONS, draftOps, mergeSummary, rebuildDiff, roomToolError, selectedRooms, type DraftState, type RoomOptions } from '../../bygning/roomTool';
import { createZoneOps } from '../../bygning/zones';
import { defaultSection, sectionThrough, type SectionLine } from '../../bygning/section';
import { DEFAULT_SLAB_OPTIONS, type SlabOptions } from '../../bygning/slabEdit';

/** Snit beside the plan (S7): off, the section alone, or the plan over the section. */
export type SectionView = 'off' | 'snit' | 'split';
import { DEFAULT_STAIR_OPTIONS, flipStairOps, stairLabel, type StairOptions } from '../../bygning/stairs';

/** The status line's snap toggles (SketchUp / Spacio): the inference engine (S3) reads them. */
export interface SnapToggles {
  /** Snap at all (Alt suspends it for one drag). */
  snap: boolean;
  /** Axis locks in the storey's Manhattan frame. */
  ortho: boolean;
  /** Detected plane traces (`GET /model/snap`). */
  trace: boolean;
  /** The 5 mm grid. */
  grid: boolean;
  /** Smart guides (frontend-u0o.12): tracking points, extensions, equal length / spacing. */
  smart: boolean;
}

export const DEFAULT_SNAP: SnapToggles = { snap: true, ortho: true, trace: true, grid: false, smart: true };

/** Mål's and Målsætning's options strips (S9). */
export interface MeasureOptions {
  /** Mål leaves a guide on the second click (Ctrl flips it for one measurement). */
  guides: boolean;
  /** How a Målsætning chain measures. */
  orient: DimOrient;
}

export const DEFAULT_MEASURE: MeasureOptions = { guides: false, orient: 'aligned' };

/**
 * A room tool's result waiting for Anvend (S6; mock 05's banner): Fra
 * vægge, Flet rum or Del rum. The drafts are drawn over the plan; nothing
 * is written until `apply`.
 */
export interface RoomPreview {
  kind: 'rebuild' | 'merge' | 'split';
  /** "Rum fra vægge", "Flet rum", "Del rum". */
  title: string;
  /** "5 rum · 1 ændret · navne bevaret". */
  summary: string;
  drafts: SpaceDraft[];
  /** Per draft guid (Fra vægge): new, changed or the same. */
  states?: Record<string, DraftState>;
  /** Stored rooms the apply removes (Flet rum's second room). */
  removed: string[];
  /** The line a split cuts along (world), drawn with the pieces. */
  line?: [XY, XY];
  /** Send it (preview: false); resolves true when the server took it. */
  apply(): Promise<boolean>;
}

/** A room tool's preview: the server's answer, or its refusal in Danish. */
export type RoomToolPreview<T> = { ok: T } | { error: string };

/** A value many small things read often (the cursor's plan position): kept out of React state. */
interface Store<T> {
  get(): T;
  set(value: T): void;
  subscribe(listener: () => void): () => void;
}

function createStore<T>(initial: T): Store<T> {
  let value = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => value,
    set: (next) => {
      if (next === value) return;
      value = next;
      for (const l of listeners) l();
    },
    subscribe: (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
  };
}

export interface GeometriEditor {
  /** Rediger › Geometri is the page shown: the editor reads, draws and takes keys. */
  active: boolean;
  canEdit: boolean;
  /** The building model with every pending batch on top. */
  model: GeometriModel;
  loading: boolean;
  /** The selected guids, the primary (`?el=`) first. */
  selection: readonly string[];
  /**
   * Select model elements. The selected guides / chains go too unless
   * `annotations` says which stay (a Shift-click keeps both kinds).
   */
  select(guids: readonly string[], annotations?: readonly string[]): void;
  /** The selected guides and dimension chains (S9: Vælg picks them too), by id. */
  annotationSelection: readonly string[];
  /** Select guides / chains; the model elements stay selected only with `keepElements`. */
  selectAnnotations(ids: readonly string[], keepElements?: boolean): void;
  /** ⌘Z / ⇧⌘Z have something to take: this session's newest edit (model or guides / chains), or the server's. */
  canUndo: boolean;
  canRedo: boolean;
  tool: GeometriTool;
  setTool(tool: GeometriTool): void;
  snap: SnapToggles;
  toggleSnap(key: keyof SnapToggles): void;
  /** `GET /model/revision` (null before it is read, or on a server without the route). */
  revision: ModelRevision | null;
  /** The edit log, newest first (null before it is read). */
  history: readonly ModelEditEntry[] | null;
  pending: readonly PendingEdit[];
  /** A solids run is queued or running. */
  rederiving: boolean;
  /**
   * Send a batch (optimistic); resolves true when the server took it. A
   * refusal is toasted, or handed to `onError` (the server's own reason)
   * when one is given.
   */
  commit(ops: ModelEditOp[], label: string, opts?: { onError?: (reason: string) => void }): Promise<boolean>;
  run(command: GeometriCommand): void;
  /** What lies under the last click, for Tab. */
  setCandidates(guids: readonly string[]): void;
  /** The pointer's plan position (the status line's readout). */
  cursor: Store<XY | null>;
  /**
   * A grip drag or a typed dimension in progress (S3): what the status
   * line says instead of the tool's hint, and the drag's readout. While it
   * is set, the editor's keys are the gesture's (digits, Tab, Enter, Esc).
   */
  gesture: Store<GestureStatus | null>;
  /** `GET /model/snap` for the storey the plan shows: wall axes, Manhattan angles, plane traces (null before it is read). */
  snapTargets: ModelSnap | null;
  /** The Væg tool's options strip (S4). */
  wallOptions: WallDrawOptions;
  setWallOptions(patch: Partial<WallDrawOptions>): void;
  /** The Dør / Vindue / Åbning tools' options strips (S5), one per kind. */
  openingOptions: Record<OpeningKind, OpeningOptions>;
  setOpeningOptions(kind: OpeningKind, patch: Partial<OpeningOptions>): void;
  /** A modify command running on the plan (S4): Flyt, Rotér, Del, Forlæng / trim… (null: none). */
  modify: ModifySession | null;
  setModify(session: ModifySession | null): void;
  /** Mål / Målsætning's options (S9). */
  measureOptions: MeasureOptions;
  setMeasureOptions(patch: Partial<MeasureOptions>): void;
  /**
   * The guides and dimension chains (S9), this case's: kept in this
   * browser only until the model can store them (ReUseX reusex-8cug).
   */
  annotations: Annotations;
  annotate(action: AnnotationAction): void;
  /** The Rum tool's options strip (S6). */
  roomOptions: RoomOptions;
  setRoomOptions(patch: Partial<RoomOptions>): void;
  /** The Zone tool's zone: the one its clicks add rooms to and take them out of; null = picking a new one. */
  zoneTarget: string | null;
  setZoneTarget(guid: string | null): void;
  /** The Zone tool's picks for a new zone. */
  zonePicks: readonly string[];
  setZonePicks(guids: readonly string[]): void;
  /** Fra vægge / Flet rum / Del rum waiting for Anvend (S6). */
  roomPreview: RoomPreview | null;
  setRoomPreview(preview: RoomPreview | null): void;
  /** Ask a room tool for a preview (`preview: true`); a refusal comes back in Danish. */
  previewRooms<T>(call: (signal?: AbortSignal) => Promise<T>, signal?: AbortSignal): Promise<RoomToolPreview<T>>;
  /**
   * Apply a room tool (`preview: false`, one model edit): `call` gets the
   * base revision; `overlay` shows its result at once (the re-read wins).
   * A stale revision is re-read and replayed once.
   */
  applyRooms<R extends RoomToolApplied>(call: (base: number | null) => Promise<R>, label: string, overlay: ModelEditOp[]): Promise<R | null>;
  /** Snit (S7): shown alone, under the plan, or not at all. */
  sectionView: SectionView;
  setSectionView(view: SectionView): void;
  /** The cut A–A (world); null until one is drawn or opened. */
  section: SectionLine | null;
  setSection(line: SectionLine | null): void;
  /** Ny snitlinje: the plan's next two clicks set A and B. */
  sectionDraw: boolean;
  setSectionDraw(on: boolean): void;
  /** The Etage editor (S7) is open (right, in place of Egenskaber). */
  storeyPanel: boolean;
  setStoreyPanel(open: boolean): void;
  /** A storey's new floor / ceiling takes along the walls, rooms and floor slabs on it. */
  storeyCarry: boolean;
  setStoreyCarry(on: boolean): void;
  /** The Dæk tool's options strip (S7). */
  slabOptions: SlabOptions;
  setSlabOptions(patch: Partial<SlabOptions>): void;
  /** The Trappe tool's options strip (S8). */
  stairOptions: StairOptions;
  setStairOptions(patch: Partial<StairOptions>): void;
  /** A fresh guid for a wall, opening or space the client creates. */
  newGuid(): string;
  /** A short message in the shell's toast (a refused command's reason). */
  notify(message: string): void;
}

/** A fresh guid (crypto's where there is one). */
function freshGuid(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** The status line's view of a gesture. */
export interface GestureStatus {
  hint: { lead: string; keys: { key: string; text: string }[] };
  readout: string | null;
}

const EMPTY_MODEL: GeometriModel = { storeys: null, walls: null, openings: null, spaces: null, slabs: null };

const GeometriEditorContext = createContext<GeometriEditor | null>(null);

/** A gesture's status, for the status line. */
export function useEditorGesture(editor: GeometriEditor): GestureStatus | null {
  return useSyncExternalStore(editor.gesture.subscribe, editor.gesture.get, editor.gesture.get);
}

/** The editor, or null outside the Bygning shell. */
export function useGeometriEditor(): GeometriEditor | null {
  return useContext(GeometriEditorContext);
}

/** The cursor's plan position, for the status line's readout. */
export function useEditorCursor(editor: GeometriEditor): XY | null {
  return useSyncExternalStore(editor.cursor.subscribe, editor.cursor.get, editor.cursor.get);
}

/** A list the server may not have (an older ruxd without the route): null. */
async function orNull<T>(run: () => Promise<T>, signal: AbortSignal): Promise<T | null> {
  try {
    return await run();
  } catch (cause) {
    if (signal.aborted) throw cause;
    return null;
  }
}

export interface GeometriEditorHandle {
  run(command: GeometriCommand): void;
}

export interface GeometriEditorProviderProps {
  /** Rediger › Geometri is shown. */
  active: boolean;
  /** `?el=`: the primary selection, the shell's. */
  element: string | null;
  setElement(guid: string | null): void;
  /** Switch the Ground between the locked plan and 3D. */
  setPlan(plan: boolean): void;
  /** Frame the whole model. */
  frameAll(): void;
  notify(message: string): void;
  /** For the shell's ⌘K palette and keyboard. */
  handleRef?: Ref<GeometriEditorHandle>;
  children: ReactNode;
}

/**
 * Rediger › Geometri's editor state (frontend-u0o.9), shared by the mode's
 * chrome (tool dock, selection bar, Egenskaber, status line), the Ground's
 * plan layer and the shell's ⌘K palette: the building model (read once for
 * all of them, re-read on `model.changed`, a finished job and every write),
 * the multi-selection (its primary is the URL's `?el=`), the tool, the snap
 * toggles and the write loop — optimistic batches against the model
 * revision, a stale revision re-read and replayed once, undo / redo, the
 * edit log and the solids' re-derive.
 */
export function GeometriEditorProvider({
  active,
  element,
  setElement,
  setPlan,
  frameAll,
  notify,
  handleRef,
  children,
}: GeometriEditorProviderProps) {
  // A viewer reads the model and selects; every write is refused before the request.
  const canEdit = useCanEdit();
  const { jobs, modelChange } = useJobs();
  const finished = jobs.filter((j) => isTerminal(j.status)).length;
  // A solids run of ours (or anyone's) still going: "Dæk & tag genberegnes…".
  const rederiving = jobs.some((j) => j.stage === 'solids' && !isTerminal(j.status));

  // ------------------------------------------------------------ reads --
  // `tick` numbers the reads: a write bumps it, and a batch stays on top
  // of the model until the read it bumped to has landed.
  const [tick, setTickState] = useState(0);
  const tickRef = useRef(0);
  /** Ask for a fresh read; returns the number it will carry. */
  const bump = useCallback(() => {
    tickRef.current += 1;
    setTickState(tickRef.current);
    return tickRef.current;
  }, []);
  const eventRevision = modelChange?.revision ?? null;
  const read = useAsync(
    async (signal) => {
      if (!active) return { tick, model: EMPTY_MODEL };
      const [storeys, walls, openings, spaces, slabs, zones, stairs] = await Promise.all([
        orNull(() => api.storeys(signal), signal),
        orNull(() => api.walls(signal), signal),
        orNull(() => api.openings(signal), signal),
        orNull(() => api.spaces(signal), signal),
        orNull(() => api.slabs(signal), signal),
        orNull(() => api.zones(signal), signal),
        orNull(() => api.stairs(signal), signal),
      ]);
      return { tick, model: { storeys, walls, openings, spaces, slabs, zones, stairs } as GeometriModel };
    },
    [active, finished, tick, eventRevision],
  );
  const revisionRead = useAsync(
    (signal) => (active ? orNull(() => api.modelRevision(signal), signal) : Promise.resolve(null)),
    [active, finished, tick, eventRevision],
  );
  // What the inference engine snaps to on the storey the plan shows (S3).
  const { state: settingsState } = useSettings();
  const snapStorey = settingsState.buildingElements.storey.storeyId;
  const snapRead = useAsync(
    (signal) => (active && snapStorey !== null ? orNull(() => api.modelSnap(snapStorey, signal), signal) : Promise.resolve(null)),
    [active, snapStorey, tick, eventRevision],
  );
  const historyRead = useAsync(
    (signal) => (active ? orNull(() => api.modelHistory(undefined, signal), signal) : Promise.resolve(null)),
    [active, tick, eventRevision],
  );
  const snapStoreyRef = useRef(snapStorey);
  snapStoreyRef.current = snapStorey;
  const revision = revisionRead.data ?? null;
  const revisionRef = useRef<number | null>(null);
  // Take the read revision once per read, not on every render: a commit, undo or redo sets
  // the ref from its own answer, and a render before the re-read lands must not put the
  // older read back (a redo right after an undo then sent a stale base_revision: 409).
  const seenRevision = useRef<typeof revision>(null);
  if (revision !== seenRevision.current) {
    seenRevision.current = revision;
    if (revision) revisionRef.current = revision.revision;
  }

  const [pending, setPending] = useState<PendingEdit[]>([]);
  const loadedTick = read.data?.tick ?? -1;
  useEffect(() => setPending((p) => settle(p, loadedTick)), [loadedTick]);
  const model = useMemo(() => withPending(read.data?.model ?? EMPTY_MODEL, pending), [read.data, pending]);
  const modelRef = useRef(model);
  modelRef.current = model;

  // -------------------------------------------------------- selection --
  const [extra, setExtra] = useState<string[]>([]);
  // Guides and chains picked in Vælg (S9): not model elements, so not in `?el=`.
  const [pickedAnnotations, setPickedAnnotations] = useState<readonly string[]>([]);
  // The primary as the editor knows it at once: `?el=` follows through the router, whose
  // navigation is a transition — behind a busy Ground it landed ~0.5 s later, and the grips
  // with it. A pick draws from this; the URL catches up.
  const [primary, setPrimary] = useState<string | null>(element);
  const lastSet = useRef<string | null>(element);
  useEffect(() => {
    // Someone else moved `?el=` (the 3D pick, Esc, a link): the extras go.
    if (element !== lastSet.current) {
      lastSet.current = element;
      setPrimary(element);
      setExtra([]);
      setPickedAnnotations([]);
    }
  }, [element]);
  const selection = useMemo(() => (primary === null ? [] : [primary, ...extra.filter((g) => g !== primary)]), [primary, extra]);
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  const select = useCallback(
    (guids: readonly string[], annotations: readonly string[] = []) => {
      const next = guids[0] ?? null;
      lastSet.current = next;
      setPrimary(next);
      setExtra(guids.slice(1));
      setPickedAnnotations(annotations);
      setElement(next);
    },
    [setElement],
  );
  const selectAnnotations = useCallback(
    (ids: readonly string[], keepElements = false) => {
      if (keepElements) setPickedAnnotations(ids);
      else select([], ids);
    },
    [select],
  );
  // Leaving Geometri drops the picks.
  useEffect(() => {
    if (!active) {
      setExtra([]);
      setPickedAnnotations([]);
    }
  }, [active]);

  const [tool, setToolState] = useState<GeometriTool>('select');
  const [modify, setModifyState] = useState<ModifySession | null>(null);
  const modifyRef = useRef<ModifySession | null>(null);
  modifyRef.current = modify;
  const setModify = useCallback((s: ModifySession | null) => {
    modifyRef.current = s;
    setModifyState(s);
  }, []);
  const [wallOptions, setWallOptionsState] = useState<WallDrawOptions>(DEFAULT_WALL_OPTIONS);
  const setWallOptions = useCallback((patch: Partial<WallDrawOptions>) => setWallOptionsState((o) => ({ ...o, ...patch })), []);
  const [openingOptions, setOpeningOptionsState] = useState<Record<OpeningKind, OpeningOptions>>(() => ({
    door: defaultOpeningOptions('door'),
    window: defaultOpeningOptions('window'),
    opening: defaultOpeningOptions('opening'),
  }));
  const setOpeningOptions = useCallback(
    (kind: OpeningKind, patch: Partial<OpeningOptions>) => setOpeningOptionsState((all) => ({ ...all, [kind]: { ...all[kind], ...patch } })),
    [],
  );
  const [measureOptions, setMeasureOptionsState] = useState<MeasureOptions>(DEFAULT_MEASURE);
  const setMeasureOptions = useCallback((patch: Partial<MeasureOptions>) => setMeasureOptionsState((o) => ({ ...o, ...patch })), []);

  // Guides and dimension chains: per user and case, in this browser (S9).
  const user = useAuth()?.me.user.id ?? null;
  const annotationsStoreKey = annotationsKey(user, api.caseId ?? 'lokal');
  // Kept in a ref as well as state: an edit records its ⌘Z step at once, before React renders it.
  const [annotationHistory, setAnnotationHistory] = useState<AnnotationHistory>(() => initialHistory(restoreAnnotations(annotationsStoreKey, browserStorage())));
  const historyRef = useRef(annotationHistory);
  const journal = useRef<Journal>(EMPTY_JOURNAL);
  /** Apply `action` to the list; true when it changed anything. */
  const applyAnnotations = useCallback((action: Parameters<typeof historyReducer>[1]): boolean => {
    const next = historyReducer(historyRef.current, action);
    if (next === historyRef.current) return false;
    historyRef.current = next;
    setAnnotationHistory(next);
    return true;
  }, []);
  const annotate = useCallback(
    (action: AnnotationAction) => {
      if (applyAnnotations(action) && action.type !== 'load') journal.current = record(journal.current, { annotations: true }).journal;
    },
    [applyAnnotations],
  );
  const annotations = annotationHistory.present;
  const annotationsLoaded = useRef(annotationsStoreKey);
  useEffect(() => {
    if (annotationsLoaded.current === annotationsStoreKey) return;
    annotationsLoaded.current = annotationsStoreKey;
    applyAnnotations({ type: 'load', state: restoreAnnotations(annotationsStoreKey, browserStorage()) });
    journal.current = EMPTY_JOURNAL;
  }, [annotationsStoreKey, applyAnnotations]);
  // A removed (or undone) guide or chain leaves the selection.
  const annotationSelection = useMemo(() => existingIds(annotations, pickedAnnotations), [annotations, pickedAnnotations]);
  const annotationSelectionRef = useRef(annotationSelection);
  annotationSelectionRef.current = annotationSelection;
  // Keyed on the state only: after a case switch the old case's list must not land under the new key.
  useEffect(() => {
    storeAnnotations(annotationsLoaded.current, annotations, browserStorage());
  }, [annotations]);

  // Rum and Zone (S6).
  const [roomOptions, setRoomOptionsState] = useState<RoomOptions>(DEFAULT_ROOM_OPTIONS);
  const setRoomOptions = useCallback((patch: Partial<RoomOptions>) => setRoomOptionsState((o) => ({ ...o, ...patch })), []);
  const [zoneTarget, setZoneTarget] = useState<string | null>(null);
  const [zonePicks, setZonePicksState] = useState<readonly string[]>([]);
  const setZonePicks = useCallback((g: readonly string[]) => setZonePicksState(g), []);
  const [roomPreview, setRoomPreviewState] = useState<RoomPreview | null>(null);
  const roomPreviewRef = useRef<RoomPreview | null>(null);
  const setRoomPreview = useCallback((p: RoomPreview | null) => {
    roomPreviewRef.current = p;
    setRoomPreviewState(p);
  }, []);

  // Snit, Etager and Dæk (S7).
  const [sectionView, setSectionViewState] = useState<SectionView>('off');
  const sectionViewRef = useRef<SectionView>('off');
  const setSectionView = useCallback((v: SectionView) => {
    sectionViewRef.current = v;
    setSectionViewState(v);
  }, []);
  const [section, setSectionState] = useState<SectionLine | null>(null);
  const sectionRef = useRef<SectionLine | null>(null);
  const setSection = useCallback((l: SectionLine | null) => {
    sectionRef.current = l;
    setSectionState(l);
  }, []);
  const [sectionDraw, setSectionDrawState] = useState(false);
  const sectionDrawRef = useRef(false);
  const setSectionDraw = useCallback((on: boolean) => {
    sectionDrawRef.current = on;
    setSectionDrawState(on);
  }, []);
  const [storeyPanel, setStoreyPanel] = useState(false);
  const [storeyCarry, setStoreyCarry] = useState(true);
  const [slabOptions, setSlabOptionsState] = useState<SlabOptions>(DEFAULT_SLAB_OPTIONS);
  const setSlabOptions = useCallback((patch: Partial<SlabOptions>) => setSlabOptionsState((o) => ({ ...o, ...patch })), []);
  // Trappe (S8).
  const [stairOptions, setStairOptionsState] = useState<StairOptions>(DEFAULT_STAIR_OPTIONS);
  const setStairOptions = useCallback((patch: Partial<StairOptions>) => setStairOptionsState((o) => ({ ...o, ...patch })), []);

  const [snap, setSnap] = useState<SnapToggles>(DEFAULT_SNAP);
  const toggleSnap = useCallback((key: keyof SnapToggles) => setSnap((s) => ({ ...s, [key]: !s[key] })), []);
  const setTool = useCallback(
    (t: GeometriTool) => {
      const def = commandDef(`tool:${t}`);
      if (!def.ready) {
        notify(`${def.label} kommer snart`);
        return;
      }
      // A tool ends a running modify command; drawing happens on the plan.
      setModify(null);
      setRoomPreview(null);
      if (t !== 'zone') {
        setZonePicksState([]);
        setZoneTarget(null);
      }
      setToolState(t);
      setSectionDraw(false);
      if (t !== 'select') {
        setPlan(true);
        // The tools draw on the plan: a section shown alone moves under it.
        if (sectionViewRef.current === 'snit') setSectionView('split');
      }
    },
    [notify, setModify, setPlan, setRoomPreview, setSectionDraw, setSectionView],
  );
  // Leaving Geometri ends the tool and any command.
  useEffect(() => {
    if (!active) {
      setModify(null);
      setToolState('select');
      setRoomPreview(null);
      setZonePicksState([]);
      setZoneTarget(null);
      setSectionView('off');
      setSectionDraw(false);
      setStoreyPanel(false);
    }
  }, [active, setModify, setRoomPreview, setSectionView, setSectionDraw]);
  const toolRef = useRef(tool);
  toolRef.current = tool;
  const candidates = useRef<readonly string[]>([]);
  const setCandidates = useCallback((guids: readonly string[]) => {
    candidates.current = guids;
  }, []);
  const cursor = useMemo(() => createStore<XY | null>(null), []);
  const gesture = useMemo(() => createStore<GestureStatus | null>(null), []);

  // ------------------------------------------------------------ writes --
  const nextId = useRef(1);
  /**
   * Send a batch; `entry`: the journal step it joins (a mixed delete), else
   * it is a step of its own. A refusal is toasted, or handed to `onError`.
   */
  const commitBatch = useCallback(
    async (ops: ModelEditOp[], label: string, entry: number | null, opts?: { onError?: (reason: string) => void }): Promise<boolean> => {
      if (!canEdit || ops.length === 0) return false;
      const id = nextId.current++;
      setPending((p) => [...p, { id, label, ops, state: 'sending' }]);
      const send = (base: number | null) =>
        api.applyModelEdits({ ...(base !== null ? { base_revision: base } : {}), label, ops });
      try {
        let result;
        try {
          result = await send(revisionRef.current);
        } catch (cause) {
          // A stale revision: read it again and replay the batch once.
          if (!(cause instanceof ApiRequestError) || failureKind(cause.status, cause.code) !== 'conflict') throw cause;
          const now = await api.modelRevision();
          revisionRef.current = now.revision;
          result = await send(now.revision);
        }
        revisionRef.current = result.revision;
        // What the server left for the user to fix (a dropped join, a hostless opening): one toast, all of it.
        if (result.warnings.length > 0) notify(`${label}: ${result.warnings.map((w) => warningText(w, modelRef.current)).join(' · ')}`);
        const next = bump();
        setPending((p) => p.map((e) => (e.id === id ? { ...e, state: 'saved', tick: next } : e)));
        journal.current = entry !== null ? markModel(journal.current, entry) : record(journal.current, { model: true }).journal;
        return true;
      } catch (cause) {
        setPending((p) => p.filter((e) => e.id !== id));
        const kind = cause instanceof ApiRequestError ? failureKind(cause.status, cause.code) : 'error';
        if (opts?.onError && kind === 'invalid') opts.onError(cause instanceof Error ? cause.message : '');
        else notify(failureMessage(kind, label, cause instanceof Error ? cause.message : undefined));
        bump();
        return false;
      }
    },
    [canEdit, notify, bump],
  );
  const commit = useCallback(
    (ops: ModelEditOp[], label: string, opts?: { onError?: (reason: string) => void }) => commitBatch(ops, label, null, opts),
    [commitBatch],
  );

  /** Snit through the selection (or the shown storey's default cut), under the plan. */
  const openSection = useCallback(
    (redraw: boolean) => {
      const m = modelRef.current;
      const sel = selectionRef.current;
      const line = (sel.length > 0 ? sectionThrough(m, sel[0]) : null) ?? (redraw || !sectionRef.current ? defaultSection(m, snapStoreyRef.current) : sectionRef.current);
      if (!line) {
        notify('Intet at snitte på denne etage');
        return;
      }
      setSection(line);
      setPlan(true);
      if (sectionViewRef.current === 'off') setSectionView('split');
    },
    [notify, setSection, setSectionView, setPlan],
  );

  // Room tools (S6): `/spaces/rebuild`, `/merge`, `/split` — previews read, applies write one model edit.
  const previewRooms = useCallback(async <T,>(call: (signal?: AbortSignal) => Promise<T>, signal?: AbortSignal): Promise<RoomToolPreview<T>> => {
    try {
      return { ok: await call(signal) };
    } catch (cause) {
      if (signal?.aborted) throw cause;
      if (cause instanceof ApiRequestError && cause.status === 422) return { error: roomToolError(cause.message) };
      const kind = cause instanceof ApiRequestError ? failureKind(cause.status, cause.code) : 'error';
      return { error: failureMessage(kind, 'Forhåndsvisningen', cause instanceof Error ? cause.message : undefined) };
    }
  }, []);

  const applyRooms = useCallback(
    async <R extends RoomToolApplied>(call: (base: number | null) => Promise<R>, label: string, overlay: ModelEditOp[]): Promise<R | null> => {
      if (!canEdit) return null;
      const id = nextId.current++;
      if (overlay.length > 0) setPending((p) => [...p, { id, label, ops: overlay, state: 'sending' }]);
      try {
        let result: R;
        try {
          result = await call(revisionRef.current);
        } catch (cause) {
          if (!(cause instanceof ApiRequestError) || failureKind(cause.status, cause.code) !== 'conflict') throw cause;
          const now = await api.modelRevision();
          revisionRef.current = now.revision;
          result = await call(now.revision);
        }
        if (typeof result.revision === 'number') revisionRef.current = result.revision;
        if (result.warnings && result.warnings.length > 0) notify(`${label}: ${result.warnings.map((w) => warningText(w, modelRef.current)).join(' · ')}`);
        const next = bump();
        setPending((p) => p.map((e) => (e.id === id ? { ...e, state: 'saved', tick: next } : e)));
        journal.current = record(journal.current, { model: true }).journal;
        return result;
      } catch (cause) {
        setPending((p) => p.filter((e) => e.id !== id));
        if (cause instanceof ApiRequestError && cause.status === 422) notify(roomToolError(cause.message));
        else {
          const kind = cause instanceof ApiRequestError ? failureKind(cause.status, cause.code) : 'error';
          notify(failureMessage(kind, label, cause instanceof Error ? cause.message : undefined));
        }
        bump();
        return null;
      }
    },
    [canEdit, notify, bump],
  );

  /** Flet rum: the merge of two selected rooms, previewed in the banner. */
  const mergeRooms = useCallback(
    async (a: string, b: string) => {
      const res = await previewRooms(() => api.mergeSpaces({ guids: [a, b], preview: true }));
      if ('error' in res) {
        notify(res.error);
        return;
      }
      const r = res.ok;
      setRoomPreview({
        kind: 'merge',
        title: 'Flet rum',
        summary: mergeSummary(modelRef.current, a, b, r.space),
        drafts: [r.space],
        removed: [r.removed],
        apply: async () => {
          const ok = await applyRooms(
            (base) => api.mergeSpaces({ guids: [a, b], preview: false, label: 'Flet rum', ...(base !== null ? { base_revision: base } : {}) }),
            'Flet rum',
            draftOps(modelRef.current, [r.space], [r.removed]),
          );
          if (ok) select([a]);
          return ok !== null;
        },
      });
    },
    [previewRooms, applyRooms, notify, setRoomPreview, select],
  );

  /** Rum fra vægge: the plan's storey rebuilt from its walls, previewed as a diff in the banner. */
  const roomsFromWalls = useCallback(async () => {
    const storey = snapStoreyRef.current;
    if (storey === null) {
      notify('Vælg en etage først');
      return;
    }
    setTool('room');
    const res = await previewRooms(() => api.rebuildSpaces({ storey, preview: true }));
    if ('error' in res) {
      notify(res.error);
      return;
    }
    const drafts = res.ok.spaces;
    const diff = rebuildDiff(modelRef.current, storey, drafts);
    setRoomPreview({
      kind: 'rebuild',
      title: 'Rum fra vægge',
      summary: diff.summary,
      drafts,
      states: Object.fromEntries(diff.rows.map((r) => [r.draft.guid, r.state])),
      removed: [],
      apply: async () =>
        (await applyRooms(
          (base) => api.rebuildSpaces({ storey, preview: false, label: 'Rum fra vægge', ...(base !== null ? { base_revision: base } : {}) }),
          'Rum fra vægge',
          draftOps(modelRef.current, drafts),
        )) !== null,
    });
  }, [notify, previewRooms, applyRooms, setRoomPreview, setTool]);

  const serverUndoRedo = useCallback(
    async (which: 'undo' | 'redo') => {
      if (!canEdit) return;
      const label = which === 'undo' ? 'Fortryd' : 'Gentag';
      const send = (base: number | null) => {
        const request = base !== null ? { base_revision: base } : {};
        return which === 'undo' ? api.undoModelEdit(request) : api.redoModelEdit(request);
      };
      try {
        let result;
        try {
          result = await send(revisionRef.current);
        } catch (cause) {
          // A stale revision (a solids run raises it too, frontend-u0o.13): read it again and
          // send once more, as an edit does. Busy / nothing to undo are not retried.
          if (!(cause instanceof ApiRequestError) || failureKind(cause.status, cause.code) !== 'conflict') throw cause;
          const now = await api.modelRevision();
          revisionRef.current = now.revision;
          result = await send(now.revision);
        }
        revisionRef.current = result.revision;
      } catch (cause) {
        const kind = cause instanceof ApiRequestError ? failureKind(cause.status, cause.code) : 'error';
        notify(failureMessage(kind, label, cause instanceof Error ? cause.message : undefined));
      }
      bump();
    },
    [canEdit, notify, bump],
  );

  /**
   * ⌘Z / ⇧⌘Z: the newest of this session's edits, whichever kind — the
   * guides and chains step back here, the model on the server, a mixed
   * delete both; with none of ours left, the server's own.
   */
  const undoRedo = useCallback(
    (which: 'undo' | 'redo') => {
      const taken = which === 'undo' ? takeUndo(journal.current) : takeRedo(journal.current);
      journal.current = taken.journal;
      const entry = taken.entry;
      if (entry?.annotations) applyAnnotations({ type: which });
      if (!entry || entry.model) void serverUndoRedo(which);
    },
    [applyAnnotations, serverUndoRedo],
  );

  const rederive = useCallback(async () => {
    if (!canEdit) return;
    try {
      await api.rederiveSolids();
      notify('Dæk og tag genberegnes…');
    } catch (cause) {
      notify(failureMessage('error', 'Genberegning', cause instanceof Error ? cause.message : undefined));
    }
  }, [canEdit, notify]);

  const run = useCallback(
    (command: GeometriCommand) => {
      const sel = selectionRef.current;
      const m = modelRef.current;
      if (command === 'tool:zone' && canEdit) {
        // Z with rooms selected: they become a zone at once, and the Zone tool goes on editing it.
        const rooms = selectedRooms(m, sel);
        setTool('zone');
        if (rooms.length > 0) {
          const guid = freshGuid();
          const made = createZoneOps(m, rooms.map((r) => r.guid), guid);
          if ('ops' in made) {
            setZoneTarget(guid);
            void commit(made.ops, made.label);
          }
        }
        return;
      }
      if (command.startsWith('tool:')) {
        setTool(command.slice(5) as GeometriTool);
        return;
      }
      // Rooms (S6): Sammenføj on two rooms merges them, Del on one cuts it with a line.
      const rooms = selectedRooms(m, sel);
      if (canEdit && rooms.length > 0 && rooms.length === sel.length && (command === 'merge' || command === 'split')) {
        if (command === 'merge') {
          if (rooms.length !== 2) notify('Vælg to rum for at flette dem');
          else void mergeRooms(rooms[0].guid, rooms[1].guid);
        } else if (rooms.length !== 1) notify('Vælg ét rum for at dele det');
        else {
          setTool('room');
          setRoomOptions({ mode: 'split' });
        }
        return;
      }
      switch (command) {
        case 'delete': {
          // In Mål / Målsætning, ⌫ takes back the newest guide / chain on the shown storey.
          if (toolRef.current === 'measure' || toolRef.current === 'dimension') {
            annotate({ type: 'remove-last', what: toolRef.current === 'measure' ? 'guides' : 'dims', storeyId: snapStoreyRef.current });
            return;
          }
          // Vælg: the selected guides and chains (local, a viewer's too) and the selected elements, as one ⌘Z step.
          const picked = annotationSelectionRef.current;
          const ops = canEdit ? deleteOps(m, sel) : [];
          if (ops.length === 0 && picked.length === 0) return;
          const pickedName = picked.length === 1 ? annotationLabel(historyRef.current.present, picked[0]) : null;
          let entry: number | null = null;
          if (picked.length > 0 && applyAnnotations({ type: 'remove-many', ids: picked })) {
            const r = record(journal.current, { annotations: true });
            journal.current = r.journal;
            entry = r.id;
          }
          select([]);
          if (ops.length > 0) void commitBatch(ops, editLabel('Slet', m, sel), entry);
          else if (pickedName) notify(`${pickedName.split(' · ')[0]} slettet · ⌘Z fortryder`);
          else notify(`${picked.length} mål og hjælpelinjer slettet · ⌘Z fortryder`);
          return;
        }
        case 'revert': {
          const ops = revertOps(m, sel);
          if (ops.length > 0) void commit(ops, editLabel('Tilbage til detekteret', m, sel));
          return;
        }
        case 'undo':
        case 'redo':
          void undoRedo(command);
          return;
        case 'escape':
          if (sectionDrawRef.current) setSectionDraw(false);
          else if (roomPreviewRef.current) setRoomPreview(null);
          else if (modifyRef.current) setModify(null);
          else if (toolRef.current !== 'select') setToolState('select');
          else if (sel.length > 0 || annotationSelectionRef.current.length > 0) select([]);
          return;
        case 'move':
        case 'copy':
        case 'rotate':
        case 'mirror':
        case 'offset':
        case 'split':
        case 'merge':
        case 'join':
        case 'trim':
        case 'align': {
          if (!canEdit) return;
          // A stair or a slab takes none of these (MV on a stair said nothing and moved nothing).
          if (sel.length > 0 && !applicableCommands(m, sel).has(command as ModifyCommand)) {
            notify(`${commandDef(command).label} gælder ikke for det valgte`);
            return;
          }
          const started = startModify(m, command as ModifyCommand, sel);
          if ('session' in started) {
            setToolState('select');
            setModify(started.session);
            setPlan(true);
          } else if ('error' in started.ops) notify(started.ops.error);
          else {
            // A merge removes the second wall: the selection keeps the one that stays.
            const merged = started.ops.ops.find((o) => o.op === 'wall.merge');
            if (merged && 'guids' in merged) select([merged.guids[0]]);
            void commit(started.ops.ops, started.label);
          }
          return;
        }
        case 'cycle':
        case 'cycle-back': {
          // Under the last click may lie guides and chains as well as elements: Tab walks them all.
          const pickedOne = annotationSelectionRef.current.length === 1 && sel.length === 0 ? annotationSelectionRef.current[0] : null;
          const next = cycle(candidates.current, sel[0] ?? pickedOne, command === 'cycle' ? 1 : -1);
          if (next === null) return;
          if (historyRef.current.present.dims.some((d) => d.id === next) || historyRef.current.present.guides.some((g) => g.id === next)) select([], [next]);
          else select([next]);
          return;
        }
        case 'view:plan':
          setSectionView('off');
          setPlan(true);
          return;
        case 'view:3d':
          setSectionView('off');
          setSectionDraw(false);
          setPlan(false);
          return;
        case 'view:snit':
        case 'view:split':
          if (!sectionRef.current) openSection(true);
          setSectionView(command === 'view:snit' ? 'snit' : 'split');
          setPlan(true);
          return;
        case 'section':
          openSection(false);
          return;
        case 'section-draw':
          setSectionView('split');
          setPlan(true);
          setSectionDraw(true);
          return;
        case 'storeys':
          setStoreyPanel((o) => !o);
          return;
        case 'zoom-fit':
          frameAll();
          return;
        case 'rederive':
          void rederive();
          return;
        case 'rooms-from-walls':
          if (canEdit) void roomsFromWalls();
          return;
        case 'flip': {
          // Vend (S8): the selected stairs walked the other way, one step.
          const stairs = (m.stairs ?? []).filter((t) => sel.includes(t.guid));
          if (!canEdit || stairs.length === 0) return;
          void commit(stairs.flatMap(flipStairOps), stairs.length === 1 ? `Vend · ${stairLabel(stairs[0])}` : `Vend ${stairs.length} trapper`);
          return;
        }
        default: {
          const def = commandDef(command);
          if (!def.ready) notify(`${def.label} (${def.keys}) kommer snart`);
        }
      }
    },
    [setTool, select, commit, commitBatch, annotate, applyAnnotations, undoRedo, setPlan, frameAll, rederive, notify, canEdit, setModify, mergeRooms, roomsFromWalls, setRoomPreview, setRoomOptions, openSection, setSectionView, setSectionDraw],
  );
  useImperativeHandle(handleRef, () => ({ run }), [run]);

  // The journal is a ref: these follow the renders its edits cause (the list's state, the revision's read).
  const serverUndo = canEdit && (revision?.can_undo ?? false);
  const serverRedo = canEdit && (revision?.can_redo ?? false);
  const canUndo = serverUndo || canUndoAnnotations(annotationHistory);
  const canRedo = serverRedo || canRedoAnnotations(annotationHistory);

  const history = useMemo(() => (historyRead.data ? [...historyRead.data.entries].reverse() : null), [historyRead.data]);

  const value = useMemo<GeometriEditor>(
    () => ({
      active,
      canEdit,
      model,
      loading: active && read.data?.model === undefined,
      selection,
      select,
      annotationSelection,
      selectAnnotations,
      canUndo,
      canRedo,
      tool,
      setTool,
      snap,
      toggleSnap,
      revision,
      history,
      pending,
      rederiving,
      commit,
      run,
      setCandidates,
      cursor,
      gesture,
      snapTargets: snapRead.data ?? null,
      wallOptions,
      setWallOptions,
      openingOptions,
      setOpeningOptions,
      modify,
      setModify,
      measureOptions,
      setMeasureOptions,
      annotations,
      annotate,
      roomOptions,
      setRoomOptions,
      zoneTarget,
      setZoneTarget,
      zonePicks,
      setZonePicks,
      roomPreview,
      setRoomPreview,
      previewRooms,
      applyRooms,
      sectionView,
      setSectionView,
      section,
      setSection,
      sectionDraw,
      setSectionDraw,
      storeyPanel,
      setStoreyPanel,
      storeyCarry,
      setStoreyCarry,
      slabOptions,
      setSlabOptions,
      stairOptions,
      setStairOptions,
      newGuid: freshGuid,
      notify,
    }),
    [active, canEdit, model, read.data, selection, select, annotationSelection, selectAnnotations, canUndo, canRedo, tool, setTool, snap, toggleSnap, revision, history, pending, rederiving, commit, run, setCandidates, cursor, gesture, snapRead.data, wallOptions, setWallOptions, openingOptions, setOpeningOptions, modify, setModify, measureOptions, setMeasureOptions, annotations, notify, roomOptions, setRoomOptions, zoneTarget, zonePicks, setZonePicks, roomPreview, setRoomPreview, previewRooms, applyRooms, sectionView, setSectionView, section, setSection, sectionDraw, setSectionDraw, storeyPanel, storeyCarry, slabOptions, setSlabOptions, stairOptions, setStairOptions],
  );
  return <GeometriEditorContext.Provider value={value}>{children}</GeometriEditorContext.Provider>;
}
